import { useEffect, useRef } from "react";
import socket from "./socket";
import { pushOrReplaceAtTop, setRequestNotification } from "../../../Slices/notification";
import { useDispatch, useSelector } from "react-redux";
import { message, notification } from "antd";
import { useExtractclientmail } from "../Outlook/fetchClientMail";
import { addNotification, fetchNotifiesThunk } from "../../../Slices/socketemail";

const SES_STATUS = {
  sent:       { label: 'Sent',       icon: '📤', color: '#605e5c' },
  delivered:  { label: 'Delivered',  icon: '✅', color: '#107c10' },
  opened:     { label: 'Opened',     icon: '👁', color: '#0078d4' },
  bounced:    { label: 'Bounced',    icon: '↩️', color: '#c50f1f' },
  clicked:    { label: 'Clicked',    icon: '🔗', color: '#7f439c' },
  replied:    { label: 'Replied',    icon: '↩️', color: '#107c10' },
  complained: { label: 'Complained', icon: '⚠️', color: '#d13438' },
};

function parseDevice(ua = '') {
  if (!ua) return '';
  const u = ua.toLowerCase();
  if (u.includes('iphone') || (u.includes('android') && u.includes('mobile'))) return '📱 Mobile';
  if (u.includes('ipad') || u.includes('tablet')) return '📱 Tablet';
  if (u.includes('mac')) return '🍎 Mac';
  if (u.includes('windows')) return '🪟 Windows';
  return '💻 Desktop';
}
export default function NotificationListener() {
  const dispatch = useDispatch();
  const audioRef = useRef(null);
  const emailRef = useRef();
  const email = useSelector((state) => state.EmailData.MailList);

  const token = localStorage.getItem("token");

  const parseJwt = (token) => {
    try {
      return JSON.parse(atob(token.split(".")[1]));
    } catch (e) {
      return null;
    }
  };
  const requestMails = useExtractclientmail();
  // Keep latest email list in a ref
  useEffect(() => {
    const merged = [...email, ...requestMails];
    emailRef.current = [...new Set(merged)];
  }, [email, requestMails]);

  useEffect(() => {
    const userId = parseJwt(token)?.id;
    if (userId) {
      dispatch(fetchNotifiesThunk({ userId }));
    }
  }, [dispatch, token]);


  // 🔔 Common function to play audio + show toast
  const triggerNotification = (content, type = "info") => {
    if (type === "success") {
      message.success({ content, duration: 5 });
    } else {
      message.info({ content, duration: 5 });
    }

    if (audioRef.current) {
      audioRef.current.play().catch((err) => {
        console.warn("🔇 Audio play error:", err.message);
      });
    }
  };

  useEffect(() => {
    socket.connect();

    socket.on("connect", () => {
      console.log("✅ Socket connected:", socket.id, parseJwt(token)?.id);
      socket.emit("join", { userId: parseJwt(token)?.id });
    });

    socket.on("disconnect", (reason) => {
      console.log("⚠️ Socket disconnected:", socket.id, reason);
    });


    const handleNewGraphNotification = (incomingData) => {
      const [firstRecipient] = incomingData.toRecipients || [];
      const recipientEmail = firstRecipient?.emailAddress?.address || "";
      const matched = emailRef.current?.some(
        (item) => item.toLowerCase().trim() === recipientEmail?.toLowerCase().trim()
      );
      if (matched) {
        dispatch(
          pushOrReplaceAtTop({
            user: recipientEmail,
            folder: "inbox",
            email: incomingData,
          })
        );
        dispatch(
          setRequestNotification(recipientEmail)
        );
        triggerNotification(`📧 You received a new mail to ${recipientEmail}`, "success");
      }
    }

    socket.on("newGraphNotification", handleNewGraphNotification);

    socket.on("leave-approved", (data) => {
      dispatch(addNotification(data));
      triggerNotification(` ${data.message}`, "info");
    });
    socket.on("routes-update", (data) => {
      triggerNotification(` ${data.message}`, "info");
    });
    socket.on("new-leave-request", (data) => {
      dispatch(addNotification(data));
    })
    socket.on("missed-call", (data) => {
      dispatch(addNotification(data));
    });
    socket.on("jobRequisition_request", (data) => {
      dispatch(addNotification(data));
    })
    socket.on("followup-reminder", (data) => {
      dispatch(addNotification(data));
      triggerNotification(`📋 ${data.message}`, "info");
    })
    socket.on("workflow-update", (data) => {
      dispatch(addNotification(data));
      triggerNotification(`⚙️ ${data.message}`, "info");
    })
    socket.on("new-sales-email", (data) => {
      dispatch(addNotification(data));
      triggerNotification(`📧 ${data.message}`, "info");
    })
    socket.on("new-announcement", (data) => {
      dispatch(addNotification(data));
      triggerNotification(`📢 ${data.message}`, "info");
    })
    // Inbound enquiry from a partner referral page (fp.blueplanitcorp.com)
    socket.on("referral:enquiry", (data) => {
      dispatch(addNotification(data));
      triggerNotification(`🤝 ${data.message}`, "info");
    })

    const handleSesEvent = (evt) => {
      // Don't show notifications if user is logged out
      if (!localStorage.getItem('token')) return;

      const cfg = SES_STATUS[evt.status] || SES_STATUS.sent;
      const subject = evt.subject ? `"${evt.subject}"` : 'an email';
      const recipient = evt.toEmails?.[0] || '';

      let lines = [recipient];
      if (evt.status === 'opened') {
        if (evt.location?.city) lines.push(`📍 ${evt.location.city}, ${evt.location.country}`);
        if (evt.userAgent)      lines.push(parseDevice(evt.userAgent));
      } else if (evt.status === 'clicked') {
        if (evt.clickUrl) lines.push(`🔗 ${evt.clickUrl}`);
      } else if (evt.status === 'bounced' || evt.status === 'complained') {
        if (evt.bounceReason) lines.push(evt.bounceReason);
      }

      notification.open({
        message: `${cfg.icon} ${cfg.label} — ${subject}`,
        description: lines.filter(Boolean).join('\n'),
        style: { borderLeft: `4px solid ${cfg.color}`, whiteSpace: 'pre-line' },
        placement: 'bottomRight',
        duration: (evt.status === 'bounced' || evt.status === 'complained') ? 0 : 6,
      });
    };

    socket.on("email:sesEvent", handleSesEvent);

    return () => {
      socket.off("connect");
      socket.off("disconnect");
      socket.off("connected");
      socket.off("routes-update");
      socket.off("leave-approved");
      socket.off("newGraphNotification", handleNewGraphNotification);
      socket.off("followup-reminder");
      socket.off("workflow-update");
      socket.off("referral:enquiry");
      socket.off("email:sesEvent", handleSesEvent);
    };
  }, []);

  return (
    <audio
      ref={audioRef}
      src="/iphone-sms.mp3"
      preload="auto"
      style={{ display: "none" }}
    />
  );
}